import { numericFormatter } from 'react-number-format';
export { numericFormatter };
export function randomNumber(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}
export function formatNumber(number, decimalScale = 2) {
    return new Intl.NumberFormat('en-us', {
        maximumFractionDigits: decimalScale
    }).format(number);
}
/**
 * Format a number with advanced options using react-number-format.
 * Supports thousand separators, decimal scale, prefix, suffix, etc.
 */
export function formatNumberAdvanced(value, options = {}) {
    var _a;
    return numericFormatter(String(value), {
        thousandSeparator: (_a = options.thousandSeparator) !== null && _a !== void 0 ? _a : ',',
        decimalSeparator: options.decimalSeparator,
        decimalScale: options.decimalScale,
        fixedDecimalScale: options.fixedDecimalScale,
        prefix: options.prefix,
        suffix: options.suffix
    });
}
export function formatCurrency(value, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    }).format(value);
}
export function padNumber(num, size) {
    return num.toString().padStart(size, '0');
}
export function compactNumber(number) {
    return Intl.NumberFormat('en', { notation: 'compact' }).format(number);
}
export function getNumberWithOrdinalSuffix(number) {
    let suffix = 'th';
    const lastDigit = number % 10;
    const lastTwoDigits = number % 100;
    if (lastTwoDigits < 11 || lastTwoDigits > 13) {
        switch (lastDigit) {
            case 1:
                suffix = 'st';
                break;
            case 2:
                suffix = 'nd';
                break;
            case 3:
                suffix = 'rd';
        }
    }
    return `${number}${suffix}`;
}
/**
 * Round a number to a given number of decimals, default is 2
 * @param number - The number to round
 * @param decimals - The number of decimals to round to
 * @returns The rounded number
 */
export const roundToDecimals = (number, decimals = 2) => {
    const factor = Math.pow(10, decimals);
    return Math.round(number * factor) / factor;
};
/**
 * Safe math expression parser (replaces eval)
 * Supports: numbers (integers, decimals, negatives), +, -, *, /, parentheses
 *
 * @param expr - Math expression string like "100 + 50 * 2" or "(100 + 50) / 2"
 * @returns The computed numeric result
 * @throws Error if expression is invalid or result is NaN
 *
 * @example
 * parseMathExpression("100 + 50") // 150
 * parseMathExpression("(100 + 50) * 2") // 300
 * parseMathExpression("100 * 2.5 - 50") // 200
 */
export function parseMathExpression(expr) {
    let pos = 0;
    const str = expr.replace(/\s+/g, ''); // Remove whitespace
    if (str.length === 0) {
        throw new Error('Empty expression');
    }
    function parseNumber() {
        let numStr = '';
        // Handle negative numbers
        if (str[pos] === '-') {
            numStr += str[pos++];
        }
        while (pos < str.length && (/\d/.test(str[pos]) || str[pos] === '.')) {
            numStr += str[pos++];
        }
        if (numStr === '' || numStr === '-') {
            throw new Error(`Expected number at position ${pos}`);
        }
        return parseFloat(numStr);
    }
    function parseFactor() {
        if (str[pos] === '(') {
            pos++; // skip '('
            const result = parseExpression();
            if (str[pos] !== ')') {
                throw new Error(`Expected ')' at position ${pos}`);
            }
            pos++; // skip ')'
            return result;
        }
        return parseNumber();
    }
    function parseTerm() {
        let result = parseFactor();
        while (pos < str.length && (str[pos] === '*' || str[pos] === '/')) {
            const op = str[pos++];
            const right = parseFactor();
            result = op === '*' ? result * right : result / right;
        }
        return result;
    }
    function parseExpression() {
        let result = parseTerm();
        while (pos < str.length && (str[pos] === '+' || str[pos] === '-')) {
            const op = str[pos++];
            const right = parseTerm();
            result = op === '+' ? result + right : result - right;
        }
        return result;
    }
    const result = parseExpression();
    // Validate result is not NaN
    if (Number.isNaN(result)) {
        throw new Error(`Expression "${expr}" resulted in NaN`);
    }
    // Check if entire expression was consumed
    if (pos < str.length) {
        throw new Error(`Unexpected character '${str[pos]}' at position ${pos}`);
    }
    return result;
}
